import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from '@/components/primitives/IconButton';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: string;
}

export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = 'aspectly:max-w-xl',
}: ModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div className="aspectly:fixed aspectly:inset-0 aspectly:bg-[#00000099] aspectly:backdrop-blur-sm aspectly:z-[9999] aspectly:flex aspectly:items-center aspectly:justify-center aspectly:p-4">
      {/* Backdrop detector */}
      <div className="aspectly:absolute aspectly:inset-0 aspectly:z-0" onClick={onClose} />

      {/* Modal Box */}
      <div
        className={`aspectly:relative aspectly:z-10 aspectly:bg-surface-container-lowest aspectly:w-full ${width} aspectly:rounded-2xl aspectly:shadow-2xl aspectly:overflow-hidden aspectly:border aspectly:border-outline-variant/20 aspectly:flex aspectly:flex-col aspectly:max-h-[90vh]`}
      >
        <div className="aspectly:p-6 aspectly:border-b aspectly:border-outline-variant/10 aspectly:flex aspectly:justify-between aspectly:items-center aspectly:bg-surface-bright">
          <div>
            <h2 className="aspectly:text-xl aspectly:font-black aspectly:text-on-surface aspectly:font-headline">{title}</h2>
            {subtitle && (
              <p className="aspectly:text-xs aspectly:text-on-surface-variant aspectly:font-medium aspectly:mt-1">{subtitle}</p>
            )}
          </div>
          <IconButton icon="close" size="md" onClick={onClose} variant="ghost" />
        </div>

        <div className="aspectly:overflow-y-auto aspectly:p-8 aspectly:grow aspectly:bg-surface-container-lowest">{children}</div>

        {footer && (
          <div className="aspectly:p-6 aspectly:border-t aspectly:border-outline-variant/10 aspectly:bg-surface-container-low/50">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
